import { Wallet } from "lucide-react";
import { Input } from "@/components/ui/input";
import { quickDatePlus } from "@/components/task/constants";
import type { PaymentPromiseInput } from "@/api/client360Tasks";

/**
 * 3A Payment Promise P0-lite sub-form (§13.2 · AC-5). Appears when outcome =
 * promised_payment. CRM reminder only — NOT a finance document, NOT a 1С change.
 * promisedPaymentDate is REQUIRED; amount optional (prefilled from overdue ctx).
 */
export function PaymentPromiseFields({
  value,
  onChange,
  overdueAmount,
}: {
  value: PaymentPromiseInput;
  onChange: (v: PaymentPromiseInput) => void;
  overdueAmount?: number | null;
}) {
  const set = (patch: Partial<PaymentPromiseInput>) => onChange({ ...value, ...patch });

  return (
    <div className="rounded-lg border border-warning-foreground/20 bg-warning p-3">
      <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-warning-foreground">
        <Wallet className="h-4 w-4" aria-hidden /> Обіцянка оплати · P0-lite
      </div>

      <div className="space-y-3">
        <div>
          <label className="mb-1 block text-xs font-medium">
            Дата обіцянки <span className="text-destructive">*</span>
          </label>
          <div className="flex flex-wrap items-center gap-1.5">
            <input
              type="date"
              value={value.promisedPaymentDate}
              onChange={(e) => set({ promisedPaymentDate: e.target.value })}
              className="rounded-md border bg-background px-2.5 py-1.5 text-sm outline-none"
            />
            {(
              [
                { label: "Завтра", days: 1 },
                { label: "+3 дні", days: 3 },
                { label: "Тиждень", days: 7 },
              ] as const
            ).map((q) => (
              <button
                key={q.days}
                type="button"
                onClick={() => set({ promisedPaymentDate: quickDatePlus(q.days) })}
                className="rounded-md border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
              >
                {q.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium">
            Сума <span className="font-normal text-muted-foreground">(опціонально{overdueAmount ? ` · борг ${overdueAmount.toLocaleString("uk-UA")} ₴` : ""})</span>
          </label>
          <Input
            inputMode="decimal"
            value={value.amount ?? ""}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^\d.]/g, "");
              set({ amount: raw === "" ? null : Number(raw) });
            }}
            placeholder={overdueAmount ? String(overdueAmount) : "0"}
          />
        </div>

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={value.createFollowUp !== false}
            onChange={(e) => set({ createFollowUp: e.target.checked })}
            className="mt-0.5"
          />
          <span>
            Створити follow-up «Перевірити оплату»{value.promisedPaymentDate ? ` на ${value.promisedPaymentDate}` : ""} (виконавець: я)
          </span>
        </label>

        <p className="text-[11.5px] text-muted-foreground">
          💡 Це CRM-нагадування, НЕ фінансовий документ і НЕ зміна в 1С. status = open. Повний finance — пізніше.
        </p>
      </div>
    </div>
  );
}
